/**
 * @file PaymentBehaviourPanel.js
 * @version 1.2.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-07-27
 * @modified 2026-07-27
 *
 * Contact-modal panel: a customer's payment behaviour — "usually pays in X
 * days" on both bases (from invoice date, and vs. due date), a payment-method
 * breakdown, and recent receipts. Data from GET /api/payments/behaviour/:id
 * (payments.payments joined to invoicing.invoices). Mirrors StatementsPanel's
 * self-contained shape.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDateShort } from '../utils/dateFormatters';
import { paymentTypeLabel, paymentNature } from '../utils/paymentTypes';
import InvoiceModal from './InvoiceModal';
import './PaymentBehaviourPanel.css';

function PaymentBehaviourPanel({ contactId, contactName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewInvoiceId, setViewInvoiceId] = useState(null);

  // Jump to the standalone Payments tab (Credit & Payments page) with this
  // contact pre-loaded into the search. Dashboard owns the navigation + tab
  // switch, so we signal it via a window event (works from any modal context).
  const viewAllPayments = () => {
    window.dispatchEvent(new CustomEvent('excel:view-payments', { detail: { contactName } }));
  };

  const fetchBehaviour = useCallback(async () => {
    if (!contactId) return;
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/payments/behaviour/${contactId}`, { credentials: 'include' });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Failed to load payment behaviour');
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { fetchBehaviour(); }, [fetchBehaviour]);

  const metrics = data?.metrics;
  const hasSample = metrics && metrics.sampleSize > 0;

  // avgDaysFromDue: negative = pays before due (early), positive = late.
  const dueLabel = () => {
    const v = metrics?.avgDaysFromDue;
    if (v === null || v === undefined) return '—';
    const rounded = Math.round(Math.abs(v));
    if (Math.abs(v) < 0.5) return 'On the due date';
    return v < 0 ? `${rounded} days early` : `${rounded} days late`;
  };

  return (
    <div className="pay-behaviour-panel">
      {loading && <div className="pb-info">Loading…</div>}
      {error && <div className="pb-info pb-error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="pb-metrics">
            <div className="pb-metric">
              <div className="pb-metric-value">
                {hasSample && metrics.avgDaysFromInvoice !== null
                  ? `${Math.round(metrics.avgDaysFromInvoice)}`
                  : '—'}
                {hasSample && metrics.avgDaysFromInvoice !== null && <span className="pb-metric-unit"> days</span>}
              </div>
              <div className="pb-metric-label">Usually pays in</div>
              <div className="pb-metric-sub">from invoice date</div>
            </div>
            <div className="pb-metric">
              <div className={`pb-metric-value ${metrics?.avgDaysFromDue > 0 ? 'pb-late' : 'pb-early'}`}>
                {hasSample ? dueLabel() : '—'}
              </div>
              <div className="pb-metric-label">Against terms</div>
              <div className="pb-metric-sub">vs. due date</div>
            </div>
          </div>

          {hasSample ? (
            <div className="pb-sample-note">
              Based on {metrics.sampleSize.toLocaleString('en-GB')} receipt{metrics.sampleSize === 1 ? '' : 's'} over the last {data.windowMonths} months
              {data.groupMemberCount > 1 ? `, combined across ${data.groupMemberCount} linked accounts` : ''}.
            </div>
          ) : (
            <div className="pb-info">
              No linked receipts in the last {data?.windowMonths || 12} months
              {data?.groupMemberCount > 1 ? ` across ${data.groupMemberCount} linked accounts` : ''}.
            </div>
          )}

          {data?.methodBreakdown?.length > 0 && (
            <div className="pb-section">
              <div className="pb-section-title">Payment methods</div>
              <div className="pb-method-chips">
                {data.methodBreakdown.map((m) => (
                  <div key={m.paymentType} className="pb-method-chip" title={`${m.count} · ${formatCurrency(m.total)}`}>
                    <span className="pb-method-name">{paymentTypeLabel(m.paymentType)}</span>
                    <span className="pb-method-count">{m.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data?.recentPayments?.length > 0 && (
            <div className="pb-section">
              <div className="pb-section-head">
                <div className="pb-section-title">Recent receipts</div>
                <button className="btn-sm set-limit" onClick={viewAllPayments} title="View all payments for this contact">
                  <span className="material-symbols-rounded">open_in_new</span>
                  View all
                </button>
              </div>
              <table className="data-table pb-table">
                <thead>
                  <tr>
                    <th style={{ width: '100px' }}>Date</th>
                    <th>Method</th>
                    <th>Invoice</th>
                    <th className="num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentPayments.map((p) => (
                    <tr key={p.id}>
                      <td>{formatDateShort(p.payment_date)}</td>
                      <td>
                        {paymentNature(p)}
                        {p.reference ? <span className="pb-muted"> · {p.reference}</span> : null}
                      </td>
                      <td>
                        {p.invoice_no
                          ? (p.invoice_id
                              ? <span className="contact-link" onClick={() => setViewInvoiceId(p.invoice_id)}>{p.invoice_no}</span>
                              : p.invoice_no)
                          : '—'}
                      </td>
                      <td className="num">{formatCurrency(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {viewInvoiceId && createPortal(
        <InvoiceModal invoiceId={viewInvoiceId} onClose={() => setViewInvoiceId(null)} />,
        document.body
      )}
    </div>
  );
}

export default PaymentBehaviourPanel;
